import React, { createContext, useCallback, useContext, useState } from 'react';

export interface WizardStep<
  TWizardState extends BaseWizardState = BaseWizardState,
> {
  id: string;
  name: string;
  content:
    | ((context: WizardContextProps<TWizardState>) => React.ReactNode)
    | React.ReactNode;
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type BaseWizardState = Record<string, any>;

export interface WizardContextProps<
  TWizardState extends BaseWizardState = BaseWizardState,
> {
  steps: MinLengthArray<1, WizardStep<TWizardState>>;
  state: TWizardState | null;
  currentStep: WizardStep<TWizardState> | null;
  currentStepIndex: number | null;
  nextStep: WizardStep<TWizardState> | null;
  previousStep: WizardStep<TWizardState> | null;
  goToNextStep: (newState?: TWizardState | null) => void;
  goToPreviousStep: (newState?: TWizardState | null) => void;
  setCurrentStep: (stepId: string | null) => void;
  setState: React.Dispatch<React.SetStateAction<TWizardState | null>>;
  mergeState: (newState: TWizardState) => void;
  reset: () => void;
}

const WizardContext = createContext<WizardContextProps | null>(null);

/**
 * Provides a wizard context which can store a set of steps and navigational
 * state for switching between them.
 * @param steps An array of WizardStep that define the wizard
 * @param initialState A key-value store (optionally typed) that represents
 * any custom state for the wizard to hold.
 */
export const Wizard = <TWizardState extends BaseWizardState = BaseWizardState>({
  steps,
  initialState,
  initialStepId,
}: {
  steps: MinLengthArray<1, WizardStep<TWizardState>>;
  initialState?: TWizardState;
  initialStepId?: string;
}) => {
  const [currentStepId, setCurrentStep] = useState<string | null>(
    initialStepId ?? steps[0].id,
  );
  const [state, setState] = useState<TWizardState | null>(initialState ?? null);
  const currentStepIndex = steps.findIndex((step) => step.id === currentStepId);
  const currentStep = steps.find((step) => step.id === currentStepId) ?? null;
  const previousStep = steps[currentStepIndex - 1] ?? null;
  const nextStep = steps[currentStepIndex + 1] ?? null;

  const goToNextStep = useCallback(
    (newState?: TWizardState | null) => {
      if (typeof newState !== 'undefined') {
        setState(newState);
      }
      if (nextStep) {
        setCurrentStep(nextStep.id);
      }
    },
    [nextStep, setState, setCurrentStep],
  );
  const goToPreviousStep = useCallback(
    (newState?: TWizardState | null) => {
      if (typeof newState !== 'undefined') {
        setState(newState);
      }
      if (previousStep) {
        setCurrentStep(previousStep.id);
      }
    },
    [previousStep, setState, setCurrentStep],
  );
  const mergeState = useCallback(
    (newState: TWizardState) => {
      setState((prevState) => ({ ...prevState, ...newState }));
    },
    [setState],
  );
  const reset = useCallback(() => {
    setState(null);
    setCurrentStep(steps[0]?.id ?? null);
  }, [steps, setState, setCurrentStep]);

  const wizardContext: WizardContextProps<TWizardState> = React.useMemo(
    () => ({
      steps,
      state,
      currentStep,
      currentStepIndex,
      nextStep,
      previousStep,
      goToNextStep,
      goToPreviousStep,
      setCurrentStep,
      setState,
      mergeState,
      reset,
    }),
    [
      currentStep,
      currentStepIndex,
      goToNextStep,
      goToPreviousStep,
      mergeState,
      nextStep,
      previousStep,
      reset,
      state,
      steps,
    ],
  );
  return (
    <WizardContext.Provider value={wizardContext as WizardContextProps}>
      {currentStep?.content &&
        (typeof currentStep.content === 'function'
          ? currentStep.content(wizardContext)
          : currentStep.content)}
    </WizardContext.Provider>
  );
};

/**
 * A hook that returns the value of the current wizard context if it exists,
 * otherwise null.  Optionally pass in a stronger type for the wizard state.
 */
export function useWizard<
  TWizardState extends BaseWizardState = BaseWizardState,
>() {
  return useContext(
    WizardContext as React.Context<WizardContextProps<TWizardState> | null>,
  );
}
